import { useMemo, useState } from "react";
import { classNames, dispatchModal, wrapClick } from "utils";
import { useQuery, useReactiveVar } from "@apollo/client";
import {
  InstallationServiceOrderResolutionView,
  Modal,
  ServiceOrderHistoryView,
  InstallationServiceOrderView,
  InstallationServiceOrderPrepaymentView,
} from "components";
import { useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import { GET_SERVICE_ORDER } from "./apollo";
import { currentUserVar } from "apollo/cache/auth";

const orderTabs = [
  {
    name: "Order Details",
    href: "OrderDetails",
    activeStatuses: [
      "AssignedToSupplier",
      "AssignedToInstaller",
      "Resolved",
      "Completed",
      "Disapproved",
      "InProgress",
    ],
  },
  {
    name: "Order History",
    href: "OrderHistory",
    activeStatuses: [
      "AssignedToSupplier",
      "AssignedToInstaller",
      "Resolved",
      "Disapproved",
      "Completed",
      "InProgress",
    ],
  },
  {
    name: "Prepayment Details",
    href: "PrepaymentDetails",
    activeStatuses: [
      "AssignedToInstaller",
      "Resolved",
      "InProgress",
      "Disapproved",
      "Completed",
    ],
  },
  {
    name: "Resolution Details",
    href: "ResolutionDetails",
    activeStatuses: ["Resolved", "Disapproved", "Completed"],
  },
];

export default function ViewInstallationServiceOrderContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const [orderTab, setOrderTab] = useState("OrderDetails");
  const currentUser = useReactiveVar(currentUserVar);
  const { data, loading } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
  });

  const serviceOrder = data?.serviceOrder;

  const canPerform = useMemo(
    () => serviceOrder?.assigner?._id === currentUser?._id,
    [serviceOrder?.assigner?._id, currentUser?._id],
  );

  const isNotMeterManufacturingContractor =
    serviceOrder?.assigneeType !== "MeterManufacturingContractor";

  const __setOrderTab = (tab: string) => () => {
    setOrderTab(tab);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      hideActions={false}
      hidePadding={true}
      loading={loading}
      title="Service Order Information"
      size="4xl"
      description="Details of service order are shown below"
      renderActions={() =>
        serviceOrder?._id ? (
          <>
            {serviceOrder.status === "Pending" && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(
                  dispatchModal(serviceOrder._id, "assign"),
                )}
              >
                Assign
              </button>
            )}
            {serviceOrder.status === "AssignedToSupplier" && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(
                  dispatchModal(serviceOrder._id, "assign"),
                )}
              >
                Reassign
              </button>
            )}
            {isNotMeterManufacturingContractor &&
              ["AssignedToInstaller", "InProgress"].includes(
                serviceOrder.status,
              ) && (
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={wrapClick(
                    dispatchModal(serviceOrder._id, "assign"),
                  )}
                >
                  Reassign
                </button>
              )}
            {isNotMeterManufacturingContractor &&
              serviceOrder.status === "Resolved" &&
              canPerform && (
                <>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={wrapClick(
                      dispatchModal(serviceOrder._id, "disapprove"),
                    )}
                  >
                    Disapprove
                  </button>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={wrapClick(
                      dispatchModal(serviceOrder._id, "approve"),
                    )}
                  >
                    Approve
                  </button>
                </>
              )}
          </>
        ) : <></>
      }
    >
      {serviceOrder?._id && (
        <>
          {[
            "AssignedToSupplier",
            "AssignedToInstaller",
            "InProgress",
            "Resolved",
            "Disapproved",
            "Completed",
          ].includes(serviceOrder.status) && (
            <div className="block">
              <div className="border-b border-gray-200 bg-white px-6">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {orderTabs.map((_orderTab) => (
                    <button
                      key={_orderTab.name}
                      onClick={wrapClick(__setOrderTab(_orderTab.href))}
                      disabled={
                        !_orderTab.activeStatuses.includes(serviceOrder.status)
                      }
                      className={classNames(
                        orderTab === _orderTab.href
                          ? "border-primary-500 text-primary-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                        "whitespace-nowrap disabled:cursor-not-allowed flex py-4 px-1 border-b-2 font-medium text-sm",
                      )}
                      aria-current={
                        orderTab === _orderTab.href ? "page" : undefined
                      }
                    >
                      {_orderTab.name}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}
          <div className="flex-1 w-full min-h-[65vh] max-h-[65vh] overflow-y-auto  sm:p-6">
            {orderTab === "OrderDetails" && (
              <InstallationServiceOrderView
              installation={serviceOrder}
              />
            )}
            {orderTab === "OrderHistory" && (
              <ServiceOrderHistoryView history={serviceOrder.history} />
            )}
            {orderTab === "ResolutionDetails" && (
              <InstallationServiceOrderResolutionView
                resolution={{
                  ...serviceOrder.resolution,
                  installedMeter: serviceOrder.installedMeter,
                }}
              />
            )}
            {orderTab === "PrepaymentDetails" && (
              <InstallationServiceOrderPrepaymentView
                serviceOrder={serviceOrder}
              />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
